import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import { formatNumber } from '../../assets/common';
import logoImageWithTitle from '../../assets/images/payPlus/payPlusWithTitle.png'

const NoAuthPayList = () => {
    const toast = useRef(null);
    const { id } = useParams();
    const [paymentData, setPaymentData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [customerInfo, setCustomerInfo] = useState(null);
    const [guarantorInfo, setGuarantorInfo] = useState(null);
    const [paymentInfo, setPaymentInfo] = useState(null);
    const [paymentSummary, setPaymentSummary] = useState({
        totalAmount: 0,
        paidAmount: 0,
        remainingAmount: 0
    });
    const paymentTrackingId = id || '7a2604fd-87ab-40af-a6b2-77d0108bb5ae'; // Test ID

    const formatName = (name, surname) => {
        if (!name || !surname) return '';
        return `${name} ${surname.charAt(0)}.`;
    };

    // Calculate payment summary (total, paid, remaining)
    const calculatePaymentSummary = (payments) => {
        if (!payments || !payments.length) return { totalAmount: 0, paidAmount: 0, remainingAmount: 0 };

        const totalAmount = payments.reduce((sum, payment) => {
            return sum + (payment.totalAmountOfInstallments || 0);
        }, 0);

        const paidAmount = payments.reduce((sum, payment) => {
            return sum + (payment.isPaid && payment.isPaid.id === 1 ? (payment.totalAmountOfInstallments || 0) : 0);
        }, 0);

        return {
            totalAmount,
            paidAmount,
            remainingAmount: totalAmount - paidAmount
        };
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axiosInstance.get(`/api/v1/no-auth/pay-list/${paymentTrackingId}`);

                const filteredData = Object.values(response.data.paymentTrackingDetails || response.data)
                    .filter(item => item.status && item.status.id === 3)
                    .sort((a, b) => {
                        const dateA = a.installmentDate ? new Date(a.installmentDate) : null;
                        const dateB = b.installmentDate ? new Date(b.installmentDate) : null;

                        if (dateA && dateB) return dateA - dateB;
                        if (dateA) return -1;
                        if (dateB) return 1;

                        if (a.installmentOrder !== b.installmentOrder)
                            return a.installmentOrder - b.installmentOrder;

                        return (a.partialPaymentInstallmentOrder || 0) - (b.partialPaymentInstallmentOrder || 0);
                    });

                setPaymentData(filteredData);
                setPaymentSummary(calculatePaymentSummary(filteredData));

                if (response.data.customer) {
                    setCustomerInfo(response.data.customer);
                }

                if (response.data.guarantor) {
                    setGuarantorInfo(response.data.guarantor);
                }

                if (response.data.paymentTracking) {
                    setPaymentInfo(response.data.paymentTracking);
                }

            } catch (error) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Hata',
                    detail: 'Ödeme bilgileri yüklenirken bir hata oluştu.',
                    life: 3000
                });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [paymentTrackingId]);

    const getPaymentStatus = (rowData) => {
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        const installmentDate = rowData.installmentDate ? new Date(rowData.installmentDate) : null;
        if (installmentDate) installmentDate.setHours(0, 0, 0, 0);

        const paidDate = rowData.paidDate ? new Date(rowData.paidDate) : null;
        if (paidDate) paidDate.setHours(0, 0, 0, 0);

        if (rowData.isPaid && rowData.isPaid.id === 1) {
            if (paidDate && installmentDate) {
                const daysDiff = Math.round((paidDate - installmentDate) / (1000 * 60 * 60 * 24));

                if (daysDiff < 0) return { status: 'Erken Ödendi', class: 'payment-early' };
                if (daysDiff === 0) return { status: 'Zamanında', class: 'payment-ontime' };
                return { status: 'Geç Ödendi', class: 'payment-late' };
            }
            return { status: 'Ödendi', class: 'payment-paid' };
        } else {
            if (installmentDate && installmentDate.getTime() === currentDate.getTime()) {
                return { status: 'Bugün', class: 'payment-due-today' };
            }

            if (installmentDate && installmentDate < currentDate) {
                const daysDiff = Math.round((currentDate - installmentDate) / (1000 * 60 * 60 * 24));
                return { status: `Gecikme ${daysDiff}g`, class: 'payment-overdue' };
            }
            return { status: 'Beklemede', class: 'payment-pending' };
        }
    };

    const rowClassName = (rowData) => {
        return getPaymentStatus(rowData).class;
    };

    return (
        <>
            <style>
                {`
                    /* Reset and Base Styles */
                    * {
                        box-sizing: border-box;
                    }
                    
                    html, body {
                        min-height: 100%;
                        margin: 0;
                        padding: 0;
                        width: 100%;
                        position: relative;
                    }
                    
                    /* Flashing animation for due-today payments */
                    @keyframes flash-blue {
                        0% { background-color: rgba(59, 130, 246, 0.1); }
                        50% { background-color: rgba(59, 130, 246, 0.3); }
                        100% { background-color: rgba(59, 130, 246, 0.1); }
                    }
                    
                    /* Container Styles */
                    .payment-container {
                        min-height: 100vh;
                        background-color: #f8f9fa;
                        padding: 0.5rem;
                        background-image: url('${logoImageWithTitle}');
                        background-repeat: no-repeat;
                        background-position: 98% 98%;
                        background-size: 120px;
                        background-attachment: fixed;
                        background-blend-mode: lighten;
                        background-color: rgba(255, 255, 255, 0.95);
                        width: 100%;
                        position: relative;
                    }
                    
                    .payment-card {
                        background-color: rgba(255, 255, 255, 0.9);
                        border-radius: 0.5rem;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                        padding: 0.75rem;
                        margin-bottom: 1rem;
                        backdrop-filter: blur(5px);
                        width: 100%;
                    }
                    
                    /* Header Styles */
                    .payment-header {
                        border-bottom: 1px solid #e5e7eb;
                        padding-bottom: 1rem;
                        margin-bottom: 1rem;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        width: 100%;
                    }
                    
                    .payment-logo {
                        height: 40px;
                        margin-bottom: 0.5rem;
                        max-width: 100%;
                        object-fit: contain;
                    }
                    
                    .payment-title {
                        font-size: 1.1rem;
                        font-weight: 600;
                        color: #111827;
                        margin-bottom: 0.25rem;
                        text-align: center;
                        width: 100%;
                    }
                    
                    .payment-subtitle {
                        font-size: 0.75rem;
                        color: #6b7280;
                        text-align: center;
                        width: 100%;
                        word-break: break-word;
                    }
                    
                    /* Customer Info */
                    .customer-info {
                        background-color: #f3f4f6;
                        border-radius: 0.375rem;
                        padding: 0.75rem;
                        margin-bottom: 1rem;
                        border-left: 4px solid #3b82f6;
                    }
                    
                    .customer-info h3 {
                        font-size: 0.9rem;
                        margin: 0 0 0.5rem 0;
                        color: #1f2937;
                    }
                    
                    .customer-detail {
                        display: flex;
                        flex-direction: column;
                        gap: 0.25rem;
                    }
                    
                    .detail-label {
                        font-size: 0.7rem;
                        color: #6b7280;
                        display: block;
                    }
                    
                    .detail-value {
                        font-size: 0.8rem;
                        font-weight: 500;
                        color: #111827;
                    }
                    
                    /* Payment Info Summary - Mobile First */
                    .payment-info-summary {
                        background-color: #f8fafc;
                        border-radius: 0.375rem;
                        padding: 0.75rem;
                        margin-bottom: 1rem;
                        border-left: 4px solid #0ea5e9;
                    }
                    
                    .info-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 0.5rem;
                        font-size: 0.75rem;
                    }
                    
                    .info-item {
                        display: flex;
                        flex-direction: column;
                    }
                    
                    .info-label {
                        font-size: 0.7rem;
                        color: #64748b;
                        margin-bottom: 0.1rem;
                    }
                    
                    .info-value {
                        font-size: 0.8rem;
                        font-weight: 600;
                        color: #0f172a;
                        word-break: break-word;
                    }
                    
                    /* Payment summary totals */
                    .payment-summary-totals {
                        margin-top: 0.75rem;
                        border-top: 1px dashed #cbd5e1;
                        padding-top: 0.75rem;
                    }
                    
                    .summary-grid {
                        display: grid;
                        grid-template-columns: 1fr;
                        gap: 0.5rem;
                    }
                    
                    .summary-item {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding: 0.25rem 0;
                    }
                    
                    .remaining-amount {
                        font-weight: 700;
                        color: #dc2626;
                    }
                    
                    .paid-amount {
                        color: #10b981;
                    }
                    
                    /* Table Container - Enable Horizontal Scroll */
                    .table-container {
                        width: 100%;
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                        border-radius: 0.375rem;
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    }
                    
                    /* Table Styles - Mobile Optimized */
                    .p-datatable {
                        width: 100%;
                        min-width: 500px; /* Minimum width to prevent crushing */
                        font-size: 0.75rem;
                    }
                    
                    .p-datatable-wrapper {
                        border-radius: 0.375rem;
                        overflow: visible;
                    }
                    
                    .p-datatable .p-datatable-thead > tr > th {
                        background-color: #1f2937 !important;
                        color: white !important;
                        font-weight: 500 !important;
                        padding: 0.5rem 0.25rem !important;
                        border: none !important;
                        font-size: 0.7rem !important;
                        white-space: nowrap !important;
                        text-align: center !important;
                    }
                    
                    .p-datatable .p-datatable-tbody > tr > td {
                        padding: 0.5rem 0.25rem !important;
                        border-color: #e5e7eb !important;
                        font-size: 0.7rem !important;
                        white-space: nowrap !important;
                        text-align: center !important;
                        vertical-align: middle !important;
                    }
                    
                    /* Column Specific Widths */
                    .installment-col { min-width: 60px; }
                    .date-col { min-width: 85px; }
                    .amount-col { min-width: 90px; }
                    .status-col { min-width: 80px; }
                    .paid-date-col { min-width: 85px; }
                    
                    /* Row Colors */
                    .p-datatable .p-datatable-tbody > tr.payment-paid {
                        background-color: rgba(16, 185, 129, 0.15) !important;
                    }
                    
                    .p-datatable .p-datatable-tbody > tr.payment-early {
                        background-color: rgba(5, 150, 105, 0.2) !important;
                    }
                    
                    .p-datatable .p-datatable-tbody > tr.payment-ontime {
                        background-color: rgba(16, 185, 129, 0.15) !important;
                    }
                    
                    .p-datatable .p-datatable-tbody > tr.payment-late {
                        background-color: rgba(245, 158, 11, 0.15) !important;
                    }
                    
                    .p-datatable .p-datatable-tbody > tr.payment-overdue {
                        background-color: rgba(239, 68, 68, 0.15) !important;
                    }
                    
                    .p-datatable .p-datatable-tbody > tr.payment-pending {
                        background-color: white !important;
                    }
                    
                    .p-datatable .p-datatable-tbody > tr.payment-due-today {
                        animation: flash-blue 1.5s infinite !important;
                        box-shadow: 0 0 5px rgba(59, 130, 246, 0.3) !important;
                    }
                    
                    /* Status badges - Compact */
                    .status-badge {
                        padding: 0.15rem 0.3rem;
                        border-radius: 0.2rem;
                        font-weight: 500;
                        font-size: 0.65rem;
                        display: inline-block;
                        text-align: center;
                        white-space: nowrap;
                        line-height: 1.2;
                    }
                    
                    .status-badge.paid, .status-badge.early, .status-badge.ontime {
                        background-color: rgba(16, 185, 129, 0.2);
                        color: #065f46;
                        border: 1px solid rgba(16, 185, 129, 0.4);
                    }
                    
                    .status-badge.late {
                        background-color: rgba(245, 158, 11, 0.2);
                        color: #92400e;
                        border: 1px solid rgba(245, 158, 11, 0.4);
                    }
                    
                    .status-badge.overdue {
                        background-color: rgba(239, 68, 68, 0.2);
                        color: #b91c1c;
                        border: 1px solid rgba(239, 68, 68, 0.4);
                    }
                    
                    .status-badge.pending {
                        background-color: rgba(59, 130, 246, 0.2);
                        color: #1e40af;
                        border: 1px solid rgba(59, 130, 246, 0.4);
                    }
                    
                    .status-badge.due-today {
                        background-color: rgba(99, 102, 241, 0.2);
                        color: #3730a3;
                        border: 1px solid rgba(99, 102, 241, 0.4);
                        font-weight: 700;
                    }
                    
                    /* Installment number - Compact */
                    .installment-number {
                        background-color: #001E51;
                        color: white;
                        border-radius: 50%;
                        width: 24px;
                        height: 24px;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        font-weight: bold;
                        font-size: 0.65rem;
                        margin: 0 auto;
                    }
                    
                    /* Amount text */
                    .amount-text {
                        font-family: monospace;
                        font-size: 0.7rem;
                        font-weight: 600;
                        white-space: nowrap;
                    }
                    
                    /* Footer Styles */
                    .payment-footer {
                        font-size: 0.75rem;
                        color: #6b7280;
                        text-align: center;
                        margin-top: 1rem;
                        padding-bottom: 50px;
                        width: 100%;
                    }
                    
                    .kvkk-footer {
                        font-size: 0.7rem;
                        color: #6b7280;
                        text-align: center;
                        margin-top: 0.5rem;
                        padding-bottom: 40px;
                    }
                    
                    /* Pagination - Mobile Friendly */
                    .p-paginator {
                        flex-wrap: wrap !important;
                        justify-content: center !important;
                        padding: 0.25rem !important;
                        font-size: 0.75rem !important;
                    }
                    
                    .p-paginator-element {
                        margin: 0.1rem !important;
                        min-width: 32px !important;
                        height: 32px !important;
                    }
                    
                    /* Scroll hint */
                    .scroll-hint {
                        font-size: 0.65rem;
                        color: #6b7280;
                        text-align: center;
                        margin-bottom: 0.5rem;
                        font-style: italic;
                    }
                    
                    /* Tablet and larger screens */
                    @media (min-width: 768px) {
                        .payment-container {
                            padding: 1rem;
                            background-size: 160px;
                        }
                        
                        .payment-card {
                            padding: 1.5rem;
                        }
                        
                        .payment-title {
                            font-size: 1.5rem;
                        }
                        
                        .payment-logo {
                            height: 60px;
                        }
                        
                        .info-grid {
                            grid-template-columns: repeat(3, 1fr);
                            font-size: 0.85rem;
                        }
                        
                        .summary-grid {
                            grid-template-columns: repeat(3, 1fr);
                        }
                        
                        .p-datatable {
                            font-size: 0.85rem;
                            min-width: auto;
                        }
                        
                        .p-datatable .p-datatable-thead > tr > th,
                        .p-datatable .p-datatable-tbody > tr > td {
                            font-size: 0.85rem !important;
                            padding: 0.75rem 0.5rem !important;
                        }
                        
                        .installment-number {
                            width: 28px;
                            height: 28px;
                            font-size: 0.75rem;
                        }
                        
                        .amount-text {
                            font-size: 0.85rem;
                        }
                        
                        .status-badge {
                            font-size: 0.75rem;
                            padding: 0.25rem 0.5rem;
                        }
                        
                        .scroll-hint {
                            display: none;
                        }
                    }
                    
                    /* Large screens */
                    @media (min-width: 1024px) {
                        .payment-container {
                            padding: 2rem;
                            background-size: 200px;
                        }
                        
                        .payment-card {
                            padding: 2rem;
                        }
                        
                        .p-datatable .p-datatable-thead > tr > th,
                        .p-datatable .p-datatable-tbody > tr > td {
                            font-size: 1rem !important;
                            padding: 0.75rem 1rem !important;
                        }
                    }
                `}
            </style>

            <div className="payment-container">
                <Toast ref={toast} />

                <div className="payment-card">
                    <div className="payment-header">
                        <img src={logoImageWithTitle} alt="PayPlus Logo" className="payment-logo" />
                        <div>
                            <h1 className="payment-title">Ödeme Planı</h1>
                            <p className="payment-subtitle">
                                Referans No: {paymentTrackingId}
                            </p>
                        </div>
                    </div>

                    {customerInfo && (
                        <div className="customer-info">
                            <h3>Müşteri Bilgileri</h3>
                            <div className="customer-detail">
                                <div>
                                    <span className="detail-label">Müşteri:</span>
                                    <span className="detail-value">
                                        Sayın {formatName(customerInfo.name, customerInfo.surname)}
                                    </span>
                                </div>
                                {guarantorInfo && (
                                    <div>
                                        <span className="detail-label">Kefil:</span>
                                        <span className="detail-value">
                                            {formatName(guarantorInfo.name, guarantorInfo.surname)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {paymentInfo && (
                        <div className="payment-info-summary">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Toplam Tutar:</span>
                                    <span className="info-value">{formatNumber(paymentInfo.amount)} ₺</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Vade:</span>
                                    <span className="info-value">{paymentInfo.term} ay</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Toplam Geri Ödeme:</span>
                                    <span className="info-value">{formatNumber(paymentInfo.totalRefundAmount)} ₺</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Toplam Faiz:</span>
                                    <span className="info-value">{formatNumber(paymentInfo.totalInterestAmount)} ₺</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">İlk Ödeme:</span>
                                    <span className="info-value">
                                        {paymentInfo.dateOfFirstPay ? new Date(paymentInfo.dateOfFirstPay).toLocaleDateString('tr-TR') : '-'}
                                    </span>
                                </div>
                            </div>

                            {paymentData.length > 0 && (
                                <div className="payment-summary-totals">
                                    <div className="summary-grid">
                                        <div className="summary-item">
                                            <span className="detail-label">Toplam Taksit:</span>
                                            <span className="detail-value amount-text">
                                                {formatNumber(paymentSummary.totalAmount)} ₺
                                            </span>
                                        </div>
                                        <div className="summary-item">
                                            <span className="detail-label">Ödenen:</span>
                                            <span className="detail-value amount-text paid-amount">
                                                {formatNumber(paymentSummary.paidAmount)} ₺
                                            </span>
                                        </div>
                                        <div className="summary-item">
                                            <span className="detail-label">Kalan:</span>
                                            <span className="detail-value amount-text remaining-amount">
                                                {formatNumber(paymentSummary.remainingAmount)} ₺
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="scroll-hint">
                        ← Tabloyu sağa kaydırarak tüm bilgileri görebilirsiniz →
                    </div>

                    <div className="table-container">
                        <DataTable
                            value={paymentData}
                            className="p-datatable-sm"
                            rowClassName={rowClassName}
                            loading={loading}
                            emptyMessage="Ödeme bilgisi bulunamadı."
                            scrollable
                            scrollHeight="flex"
                        >
                            <Column
                                className="installment-col"
                                field="installmentOrder"
                                header="Taksit"
                                body={(rowData) => (
                                    <div className="installment-number">
                                        {rowData?.installmentOrder}
                                        {rowData?.isPartialPayment?.id === 1 && (
                                            `-${rowData?.partialPaymentInstallmentOrder}`
                                        )}
                                    </div>
                                )}
                            />
                            <Column
                                className="date-col"
                                field="installmentDate"
                                header="Vade"
                                body={(rowData) => (
                                    <div style={{ fontSize: '0.7rem' }}>
                                        {rowData.installmentDate
                                            ? new Date(rowData.installmentDate).toLocaleDateString('tr-TR')
                                            : ''}
                                    </div>
                                )}
                            />
                            <Column
                                className="amount-col"
                                field="totalAmountOfInstallments"
                                header="Tutar"
                                body={(rowData) => (
                                    <div className="amount-text">
                                        {rowData?.totalAmountOfInstallments
                                            ? formatNumber(rowData.totalAmountOfInstallments) + "₺"
                                            : ''}
                                    </div>
                                )}
                            />
                            <Column
                                className="status-col"
                                field="isPaid"
                                header="Durum"
                                body={(rowData) => {
                                    const status = getPaymentStatus(rowData);
                                    const statusType = status.class.replace('payment-', '');
                                    return (
                                        <span className={`status-badge ${statusType}`}>
                                            {status.status}
                                        </span>
                                    );
                                }}
                            />
                            <Column
                                className="paid-date-col"
                                field="paidDate"
                                header="Ödeme"
                                body={(rowData) => (
                                    <div style={{ fontSize: '0.7rem' }}>
                                        {rowData?.isPaid?.id === 1 && rowData?.paidDate
                                            ? new Date(rowData.paidDate).toLocaleDateString('tr-TR')
                                            : '—'}
                                    </div>
                                )}
                            />
                        </DataTable>
                    </div>
                </div>

                <div className="payment-footer">
                    <p>Bu ödeme planı bilgilendirme amaçlıdır.</p>
                    <p>Detaylı bilgi için lütfen müşteri temsilcinizle iletişime geçiniz.</p>
                </div>

                <div className="kvkk-footer">
                    <p>© {new Date().getFullYear()} PayPlus. Tüm hakları saklıdır.</p>
                </div>
            </div>
        </>
    );
};

export default NoAuthPayList;